import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Minus, Plus, Trash2, ShoppingBag, ArrowRight } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useStore } from "@/context/StoreContext";
import { getPriceInfo, cartSavings, minPurchaseFor, comboStep, freeUnits, isComboOffer } from "@/lib/pricing";
import { api, resolveImageUri, formatINR } from "@/lib/api";

export default function Cart() {
  const navigate = useNavigate();
  const { lines, setQuantity, removeItem, subtotal, shipping, total, totalBV, verified } = useStore();
  const { data: settings } = useQuery({ queryKey: ["settings"], queryFn: () => api.getSettings() });
  const savings = cartSavings(lines, verified);
  const minPurchase = settings ? (verified ? settings.min_purchase_altos : settings.min_purchase_regular) : minPurchaseFor(verified);
  const belowMin = subtotal < minPurchase;

  if (lines.length === 0) {
    return (
      <div className="min-h-screen bg-paper">
        <Header />
        <div className="mx-auto max-w-6xl px-6 py-24 text-center">
          <ShoppingBag className="mx-auto text-line" size={56} />
          <h1 className="font-display text-4xl text-ink mt-4">Your cart is empty</h1>
          <p className="text-muted2 mt-2">Discover our herbal wellness collection.</p>
          <Link to="/" className="inline-flex items-center gap-2 mt-6 bg-brand text-white font-semibold rounded-full px-7 h-12" data-testid="empty-cart-shop">
            Start shopping <ArrowRight size={18} />
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-paper">
      <Header />
      <main className="mx-auto max-w-6xl px-4 sm:px-6 py-8">
        <h1 className="font-display text-4xl sm:text-5xl text-ink mb-6">Your Cart</h1>
        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-4">
            {lines.map((l) => {
              const info = getPriceInfo(l.product, verified);
              const step = comboStep(l.product);
              const free = freeUnits(l.product, l.quantity);
              return (
                <div key={l.product.id} data-testid={`cart-line-${l.product.id}`} className="flex gap-4 rounded-xl border border-line p-4">
                  <img src={resolveImageUri(l.product.image)} alt={l.product.name} className="w-24 h-24 rounded-lg object-cover bg-mist shrink-0" />
                  <div className="flex-1 min-w-0">
                    <Link to={`/product/${l.product.id}`} className="font-display text-2xl text-ink leading-tight hover:text-brand line-clamp-1">{l.product.name}</Link>
                    <p className="text-xs text-muted2">{l.product.weight}</p>
                    {isComboOffer(l.product) && (
                      <p className="text-xs text-crimson font-semibold mt-0.5" data-testid={`cart-combo-${l.product.id}`}>
                        Buy 3 Get 1 Free{free > 0 ? ` · ${l.quantity} + ${free} free` : ""}
                      </p>
                    )}
                    <div className="flex items-center gap-2 mt-1">
                      <span className="font-semibold text-ink">{formatINR(info.unit)}</span>
                      {info.compareAt !== null && <span className="text-xs text-muted2 line-through">{formatINR(info.compareAt)}</span>}
                    </div>
                    <div className="flex items-center justify-between mt-3">
                      <div className="flex items-center border border-line rounded-full">
                        <button onClick={() => setQuantity(l.product.id, l.quantity - step)} className="w-9 h-9 flex items-center justify-center" data-testid={`cart-minus-${l.product.id}`}><Minus size={14} /></button>
                        <span className="w-7 text-center text-sm font-semibold">{l.quantity}</span>
                        <button onClick={() => setQuantity(l.product.id, l.quantity + step)} className="w-9 h-9 flex items-center justify-center" data-testid={`cart-plus-${l.product.id}`}><Plus size={14} /></button>
                      </div>
                      <button onClick={() => removeItem(l.product.id)} className="text-muted2 hover:text-err flex items-center gap-1 text-sm" data-testid={`cart-remove-${l.product.id}`}><Trash2 size={15} /> Remove</button>
                    </div>
                  </div>
                  <div className="text-right font-semibold text-ink hidden sm:block">{formatINR(info.unit * l.quantity)}</div>
                </div>
              );
            })}
          </div>

          {/* Summary */}
          <div className="rounded-2xl border border-line p-6 h-fit lg:sticky lg:top-24" data-testid="cart-summary">
            <h2 className="font-display text-2xl text-ink mb-4">Order Summary</h2>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-muted2">Subtotal</span><span className="text-ink font-medium">{formatINR(subtotal)}</span></div>
              <div className="flex justify-between"><span className="text-muted2">Shipping</span><span className="text-ink font-medium">{shipping === 0 ? "Free" : formatINR(shipping)}</span></div>
              {verified && totalBV > 0 && <div className="flex justify-between"><span className="text-muted2">Total BV</span><span className="text-brand font-semibold">{totalBV}</span></div>}
              <div className="border-t border-line my-2" />
              <div className="flex justify-between text-base"><span className="font-semibold text-ink">Total</span><span className="font-semibold text-ink">{formatINR(total)}</span></div>
            </div>
            {savings > 0 && (
              <div className="mt-3 rounded-lg bg-brandter text-ok text-sm font-semibold px-3 py-2 text-center">You're saving {formatINR(savings)} on this order!</div>
            )}
            {belowMin && (
              <div className="mt-3 rounded-lg bg-warn/10 text-warn text-sm px-3 py-2" data-testid="min-purchase-notice">
                Minimum order is {formatINR(minPurchase)}. Add {formatINR(minPurchase - subtotal)} more to checkout.
              </div>
            )}
            <button
              disabled={belowMin}
              onClick={() => navigate("/checkout")}
              data-testid="checkout-button"
              className="w-full h-12 mt-4 rounded-full bg-brand text-white font-semibold flex items-center justify-center gap-2 disabled:opacity-50 hover:bg-brand/90 transition-colors"
            >
              Proceed to Checkout <ArrowRight size={18} />
            </button>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
